import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { GroupItemType } from '@cds/betting-offer';
import { SportsThemedNotificationDirective, ThemeNotificationVariant } from '@frontend/sports/common/ui/sports-themed-notifcation-bubble';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { PriceboostCountSignpostComponent } from '@frontend/sports/price-boost/feature';
import { WidgetContext } from '@frontend/sports/types/components/widget';
import { DsArrow } from '@frontend/ui/arrow';
import { DsDivider } from '@frontend/ui/divider';
import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { DsTabsModule } from '@frontend/ui/tabsgroup';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

export enum WidgetTabBarDisplay {
    Default,
    Icon,
    Logo,
}

export interface WidgetTabBarItem {
    id: string;
    title?: string;
    icon?: string;
    url?: string;
    count?: number;
    active?: boolean;
    disabled?: boolean;
    attribute?: string;
    display: WidgetTabBarDisplay;
    context: Partial<WidgetContext>;
    itemId: string | number;
    sportId?: number;
    widgetId: string;
    showSignPost?: boolean;
    group?: {
        count?: number;
        type: GroupItemType;
        name?: string;
    };
}

@Component({
    selector: 'ms-widget-tab-bar',
    styleUrls: ['./widget-tab-bar.component.scss'],
    templateUrl: './widget-tab-bar.component.html',
    styles: [
        `
            :host {
                .icon {
                    display: flex;
                    font-size: 16px;
                }
            }
        `,
    ],
    imports: [
        DsTabsModule,
        CompetitionLogoComponent,
        PriceboostCountSignpostComponent,
        DsNotificationBubble,
        SportsThemedNotificationDirective,
        DsDivider,
        DsArrow,
        IconCustomComponent,
        NgClass,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetTabBarComponent implements OnChanges {
    @Input() tabs: WidgetTabBarItem[] = [];
    @Input() stretch = false;
    @Output() select = new EventEmitter<WidgetTabBarItem>();
    @HostBinding('class') className = 'widget-tab-bar';

    variant: ThemeNotificationVariant = {
        'default': 'neutral',
        'mgm-dice': 'utility',
        'sports-interaction': 'utility',
    };

    selectedTabBubbleVariant: ThemeNotificationVariant = {
        'default': 'primary',
        'mgm-dice': 'utility',
        'sports-interaction': 'utility',
    };

    display = WidgetTabBarDisplay;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tabs']) {
            this.updateTabTrackingIds();
        }
    }

    selected(id: string) {
        this.tabs = this.tabs.map((item) => ({
            ...item,
            active: item.attribute === id,
        }));
        const activetab = this.tabs.find((item) => item.active);
        this.select.emit(activetab);
    }

    private updateTabTrackingIds(): void {
        this.tabs = this.tabs.map((tab, index) => ({
            ...tab,
            attribute: (index + 1).toString(),
        }));
    }

    get active(): string {
        const activetab = this.tabs.find((tab) => tab.active) ?? this.tabs[0];

        return activetab.attribute?.toString() ?? '';
    }
}
